import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import PropTypes from "prop-types";

import { selectMechanicDefaultBranchId } from "../../../redux/auth";
import { clearCart } from "../../../redux/cart";

import { createOrder } from "../../../services/order";

import { APP_PATHS } from "../../../constants/paths";

import PrimaryButton from "../../UI/PrimaryButton/PrimaryButton";
import OrderSummaryModal from "../../UI/Modals/OrderSummaryModal/OrderSummaryModal";
import BranchSelectionModal from "../../UI/Modals/BranchSelectionModal/BranchSelectionModal";
import OrderSuccessModal from "../../UI/Modals/OrderSuccessModal/OrderSuccessModal";
import Price from "../../Price/Price";
import { DeliveryDateSelectionModal } from "../../UI/Modals";

import classNames from "classnames";
import classes from "./Checkout.module.css";

const Checkout = (props) => {
  const { subTotal, discount, total, skus, bundles } = props;

  const [isOrderSummaryModalOpen, setIsOrderSummaryModalOpen] = useState(false);
  const [isBranchSelectionModalOpen, setIsBranchSelectionModalOpen] =
    useState(false);
  const [
    isDeliveryDateSelectionModalOpen,
    setIsDeliveryDateSelectionModalOpen,
  ] = useState(false);
  const [isOrderSuccessModalOpen, setIsOrderSuccessModalOpen] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState();
  const defaultBranchId = useSelector(selectMechanicDefaultBranchId);
  const [selectedDelivery, setSelectedDelivery] = useState();
  const [isSubmitOrderLoading, setIsSubmitOrderLoading] = useState(false);
  const [submitOrderError, setSubmitOrderError] = useState("");

  const isCreateOrderDisabled = useMemo(() => {
    return skus.some(
      (sku) =>
        sku.quantity > sku.availableQuantity ||
        sku.suggestedSellingPrice > sku.price
    );
  }, [skus]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  function openDeliveryDateSelectionModalHandler() {
    setIsDeliveryDateSelectionModalOpen(true);
  }

  function closeDeliveryDateSelectionModal() {
    setIsDeliveryDateSelectionModalOpen(false);
  }

  function deliveryDateSelectedHandler(e) {
    const {
      target: { value, name },
    } = e;
    const selectedSlot = JSON.parse(value);
    setSelectedDelivery({
      isExpressDelivery: name === "express",
      deliverySlotFrom: selectedSlot.from,
      deliverySlotTo: selectedSlot.to,
    });
  }

  const openOrderSummaryModalHandler = () => {
    if (isCreateOrderDisabled || isSubmitOrderLoading) {
      return;
    }
    setIsOrderSummaryModalOpen(true);
  };

  const closeOrderSummaryModalHandler = () => {
    setIsOrderSummaryModalOpen(false);
  };

  const orderConfirmedHandler = () => {
    closeOrderSummaryModalHandler();
    openBranchSelectionModalHandler();
  };

  function openBranchSelectionModalHandler() {
    setIsBranchSelectionModalOpen(true);
  }

  const closeBranchSelectionModalHandler = () => {
    setIsBranchSelectionModalOpen(false);
  };

  const branchChangedHandler = (event) => {
    setSelectedBranchId(Number(event.target.value));
  };

  function selectedBranchConfirmedHandler() {
    closeBranchSelectionModalHandler();
    openDeliveryDateSelectionModalHandler();
  }

  const orderSuccessModalClosedHandler = () => {
    setIsOrderSuccessModalOpen(false);
    dispatch(clearCart());
    navigate(APP_PATHS.HOME.url);
  };

  const submitOrder = async () => {
    if (isSubmitOrderLoading || !selectedDelivery) {
      return;
    }
    try {
      setIsSubmitOrderLoading(true);
      await createOrder(selectedDelivery, selectedBranchId, skus, bundles);
      setIsDeliveryDateSelectionModalOpen(false);
      setIsOrderSuccessModalOpen(true);
    } catch (err) {
      setSubmitOrderError("حدث خطأ أثناء تسليم الطلب");
    } finally {
      setIsSubmitOrderLoading(false);
    }
  };

  useEffect(() => {
    setSelectedBranchId(defaultBranchId);
  }, [defaultBranchId]);

  return (
    <div className={classes["checkout"]}>
      <h2 className={classes["checkout__header"]}>الإجمالي</h2>
      <div className={classes["checkout__content-wrapper"]}>
        <div className={classes["checkout__content-row"]}>
          <p className={classes["checkout__label"]}>المجموع</p>
          <Price
            value={subTotal}
            suffix="جم"
            className={classes["checkout__value"]}
            suffixClassName={classes["checkout__currency"]}
          />
        </div>
        <div className={classes["checkout__content-row"]}>
          <p className={classes["checkout__label"]}>الخصومات</p>
          <Price
            value={discount}
            suffix="جم"
            className={classNames(
              classes["checkout__value"],
              classes["checkout__value--discount"]
            )}
            suffixClassName={classes["checkout__currency"]}
          />
        </div>
      </div>

      <div className={classes["checkout__separator"]} />

      <div className={classes["checkout__content-wrapper"]}>
        <div className={classes["checkout__content-row"]}>
          <p
            className={classNames(
              classes["checkout__label"],
              classes["checkout__label--total"]
            )}
          >
            الإجمالي
          </p>
          <Price
            value={total}
            suffix="جم"
            className={classes["checkout__value"]}
            suffixClassName={classes["checkout__currency"]}
          />
        </div>
      </div>

      <PrimaryButton
        withAnimation
        className={classes["checkout__btn"]}
        onClick={openOrderSummaryModalHandler}
        disabled={isCreateOrderDisabled || isSubmitOrderLoading}
      >
        إتمام عملية الشراء
      </PrimaryButton>

      <OrderSummaryModal
        isOpen={isOrderSummaryModalOpen}
        onClose={closeOrderSummaryModalHandler}
        onConfirm={orderConfirmedHandler}
        total={total}
        skus={skus}
        bundles={bundles}
      />
      <BranchSelectionModal
        isOpen={isBranchSelectionModalOpen}
        onClose={closeBranchSelectionModalHandler}
        currentSelectedBranchId={selectedBranchId}
        onChangeBranch={branchChangedHandler}
        footer={
          <div className={classes["checkout__branches-modal-footer"]}>
            <div className={classes["checkout__branches-modal-total-wrapper"]}>
              <p className={classes["checkout__branches-modal-total-text"]}>
                الإجمالي
              </p>
              <p
                className={
                  classes["checkout__branches-modal-total-value-wrapper"]
                }
              >
                <span
                  className={classNames(
                    classes["checkout__branches-modal-total-value"],
                    "numeric-content"
                  )}
                >
                  {total}
                </span>
                <span
                  className={classes["checkout__branches-modal-total-currency"]}
                >
                  جم
                </span>
              </p>
            </div>
            <div>
              <PrimaryButton
                withAnimation
                onClick={selectedBranchConfirmedHandler}
                className={classes["checkout__branches-modal-submit-btn"]}
                disabled={isSubmitOrderLoading}
              >
                تأكيد عملية الشراء
              </PrimaryButton>
            </div>
          </div>
        }
      />
      {isDeliveryDateSelectionModalOpen && (
        <DeliveryDateSelectionModal
          isOpen={isDeliveryDateSelectionModalOpen}
          onClose={closeDeliveryDateSelectionModal}
          branchId={selectedBranchId}
          skus={skus}
          bundles={bundles}
          onSelectDeliverySlot={deliveryDateSelectedHandler}
          selectedDeliverySlot={selectedDelivery}
          total={total}
          onConfirm={submitOrder}
          isSubmitOrderLoading={isSubmitOrderLoading}
          submitOrderError={submitOrderError}
        />
      )}
      <OrderSuccessModal
        isOpen={isOrderSuccessModalOpen}
        onClose={orderSuccessModalClosedHandler}
        message="تم إرسال طلبك بنجاح"
      />
    </div>
  );
};

export default Checkout;

Checkout.propTypes = {
  subTotal: PropTypes.number,
  discount: PropTypes.number,
  total: PropTypes.number,
  skus: PropTypes.array,
  bundles: PropTypes.array,
};
